import React, { PureComponent } from 'react'
import PropTypes from 'prop-types'
import { IconButton, Pane, Heading } from 'evergreen-ui'
import { get } from 'lodash'
import OptionsList from './OptionsList'
import OptionGroupShapePropType from './OptionGroupShapePropType'

export default class SelectMenuContent extends PureComponent {
  static propTypes = {
    close: PropTypes.func,
    title: PropTypes.string,
    width: PropTypes.number,
    height: PropTypes.number,
    groups: PropTypes.arrayOf(OptionGroupShapePropType),
    hasTitle: PropTypes.bool,
    hasFilter: PropTypes.bool,
    listProps: PropTypes.shape(OptionsList.propTypes),

    /**
     * Node that is placed right next to the options.
     */
    detailView: PropTypes.node
  }

  static defaultProps = {
    groups: [],
    hasTitle: true,
    hasFilter: true
  }

  componentDidMount() {
    window.addEventListener('keyup', this.handleKeyUp)
  }

  componentWillUnmount() {
    window.removeEventListener('keyup', this.handleKeyUp)
  }

  handleKeyUp = e => {
    if (e.keyCode === 38) {
      this.handleArrowUp()
    }

    if (e.keyCode === 40) {
      this.handleArrowDown()
    }

    if (e.keyCode === 13) {
      this.handleEnter()
    }
  }

  getCurrentIndex() {
    const { groups, listProps } = this.props
    const { selected } = listProps

    for (const [groupIndex, group] of groups.entries()) {
      for (const [optionIndex, option] of group.options.entries()) {
        if (option.value === selected[0]) {
          return `${groupIndex}.options[${optionIndex}]`
        }
      }
    }

    return -1
  }

  getCurrentGroupIndex() {
    const currentIndex = this.getCurrentIndex()
    if (currentIndex === -1) {
      return -1
    }

    const [groupIndex] = currentIndex.split('.')
    return Number(groupIndex)
  }

  getPrevIndex() {
    const { groups } = this.props
    const currentIndex = this.getCurrentIndex()
    if (currentIndex === -1) {
      return '0.options[0]'
    }

    const indexes = currentIndex.replace(/[^0-9.]+/g, '').split('.')
    const groupIndex = Number(indexes[0])
    const optionIndex = Number(indexes[1])

    if (optionIndex === 0) {
      if (groupIndex === 0) {
        const lastGroupIndex = groups.length - 1
        const lastOptionIndex = groups[lastGroupIndex].options.length - 1

        return `${lastGroupIndex}.options[${lastOptionIndex}]`
      } else {
        const lastOptionIndex = groups[groupIndex - 1].options.length - 1

        return `${groupIndex - 1}.options[${lastOptionIndex}]`
      }
    }

    return `${groupIndex}.options[${optionIndex - 1}]`
  }

  getNextIndex() {
    const { groups } = this.props
    const currentIndex = this.getCurrentIndex()
    if (currentIndex === -1) {
      return '0.options[0]'
    }

    const indexes = currentIndex.replace(/[^0-9.]+/g, '').split('.')
    const groupIndex = Number(indexes[0])
    const optionIndex = Number(indexes[1])

    if (optionIndex === groups[groupIndex].options.length - 1) {
      if (groupIndex === groups.length - 1) {
        return '0.options[0]'
      } else {
        return `${groupIndex + 1}.options[0]`
      }
    }

    return `${groupIndex}.options[${optionIndex + 1}]`
  }

  handleArrowUp() {
    const { groups, listProps } = this.props
    const { onSelect } = listProps

    const option = get(groups, this.getPrevIndex())
    if (option) {
      onSelect(option)
    }
  }

  handleArrowDown() {
    const { groups, listProps } = this.props
    const { onSelect } = listProps

    const option = get(groups, this.getNextIndex())
    if (option) {
      onSelect(option)
    }
  }

  handleEnter() {
    const isSelected = this.getCurrentIndex() !== -1

    if (isSelected) {
      this.props.close()
    }
  }

  render() {
    const {
      width,
      height,
      groups,
      hasTitle,
      hasFilter,
      close,
      title,
      listProps,
      detailView
    } = this.props

    const headerHeight = 40
    const optionsListHeight = hasTitle ? height - headerHeight : height
    const hasDetailView = Boolean(detailView)

    return (
      <Pane display="flex" height={height}>
        <Pane
          width={width}
          height={height}
          display="flex"
          flexDirection="column"
          {...(hasDetailView
            ? {
                borderRight: 'muted'
              }
            : {})}
        >
          {hasTitle && (
            <Pane
              display="flex"
              alignItems="center"
              borderBottom="muted"
              padding={8}
              height={headerHeight}
              boxSizing="border-box"
            >
              <Pane flex="1">
                <Heading size={400}>{title}</Heading>
              </Pane>
              <IconButton
                icon="close"
                appearance="ghost"
                height={24}
                onClick={close}
              />
            </Pane>
          )}
          <OptionsList
            height={optionsListHeight}
            hasFilter={hasFilter}
            groups={groups}
            selectedGroupIndex={this.getCurrentGroupIndex()}
            {...listProps}
          />
        </Pane>
        {hasDetailView && detailView}
      </Pane>
    )
  }
}
